/**
 * Post-pick cinematic — MVP PR A3.
 *
 * Cartridge ChooseStarterScene continues after the player picks a ball:
 * Oak confirms ("Ah! BULBASAUR is your choice…"), Oak's "energetic"
 * reaction + travel advice, then the rival walks from his (5, 4) spawn
 * to the opposing-type pedestal, "takes" it ("I'll take this one,
 * then!"), and returns to his spawn. State advances to 3 (POST_PICK)
 * so the row-8 rival-battle triggers become eligible.
 *
 * Fired by the WAKE handler's dispatch gate when:
 *
 *     mapData.name === 'PalletTown_ProfessorOaksLab'
 *     && hasFlag(STARTER_PICKED)
 *     && getLabSceneState() === 2  (PRE_PICK from oakIntercept)
 *     && !hasFlag(LAB_POST_PICK_DONE)
 *
 * Synthetic scriptRef `__lab_post_pick__` registered in scriptRunner's
 * synthetic-prefix allowlist so the dialogue-coverage lint stays clean.
 *
 * Beat sequence:
 *
 *     ┌──────────────────────────────────────────────────────────┐
 *     │  ENTRY: in-memory postPickCinematicActive guard           │
 *     │            │  (session re-entrancy only; the PERSISTED    │
 *     │            │   LAB_POST_PICK_DONE latch moves to the END  │
 *     │            │   so an interrupted cinematic retries on     │
 *     │            │   reload — third-playtest latch fix)         │
 *     │            ▼                                              │
 *     │  Oak "Ah! BULBASAUR is your choice…" (per species)        │
 *     │            ▼                                              │
 *     │  Oak "This POKéMON is really quite energetic!"             │
 *     │  Oak "If a wild POKéMON appears…" (travel advice)         │
 *     │            ▼                                              │
 *     │  delay 30 frames                                          │
 *     │            ▼                                              │
 *     │  setFlag(HIDE_RIVAL_IN_LAB) + refreshNpcVisibility        │
 *     │            ▼                                              │
 *     │  spawn walker @ (5, 4) → walk to (oppX, 5)                │
 *     │            ▼                                              │
 *     │  Rival "I'll take this one, then!"                        │
 *     │            ▼                                              │
 *     │  setFlag(HIDE_<opposingSpecies>_BALL) + refresh           │
 *     │            ▼                                              │
 *     │  "BLUE received the X!"                                   │
 *     │            ▼                                              │
 *     │  walker returns: (oppX, 5) → (5, 5) → (5, 4)              │
 *     │            ▼                                              │
 *     │  clearFlag(HIDE_RIVAL_IN_LAB) + refresh ◄── static mounts  │
 *     │            ▼                                              │
 *     │  walker.destroy()              ◄── handoff: both sprites  │
 *     │                                    at (5, 4) for one      │
 *     │                                    frame, no invisible    │
 *     │                                    gap (eng review #6)    │
 *     │            ▼                                              │
 *     │  setLabSceneState(POST_PICK)   ◄── state=3, row-8         │
 *     │  save()                            rival-battle triggers  │
 *     │                                    become eligible        │
 *     └──────────────────────────────────────────────────────────┘
 */
import type { Handler, ScriptCtx } from '../scriptRunner';
import { effectsReduced } from '../../render/motion';
import { opposingStarterFor } from '../../data/trainerParties';
import {
  setLabSceneState,
  LAB_SCENE_STATES,
} from '../labScene';

// Cartridge text per OakChoosing<Species>. Each line is two pages
// (cartridge \p separates the "X is your choice. It's <trait>." from
// "So, RED, you're claiming the <type> POKéMON <species>?").
const OAK_CONFIRM_BY_SPECIES: Record<string, string[]> = {
  bulbasaur: [
    'OAK: I see! BULBASAUR is your choice.',
    "It's very easy to raise.",
  ],
  charmander: [
    'OAK: Ah! CHARMANDER is your choice.',
    'You should raise it patiently.',
  ],
  squirtle: [
    'OAK: Hm! SQUIRTLE is your choice.',
    "It's one worth raising.",
  ],
};

// Cartridge Text_OakThisMonIsEnergetic + Text_OakCanReachNextTownWithMon.
// Run back-to-back as two dialogue groups (cartridge has them as
// separate msgbox calls).
const OAK_ENERGETIC = [
  'This POKéMON is really quite energetic!',
];
const OAK_CAN_REACH_NEXT_TOWN = [
  'OAK: If a wild POKéMON appears,',
  'your POKéMON can battle it.',
  'With it at your side, you should be able',
  'to reach the next town.',
];

// Cartridge Text_RivalIllTakeThisOneThen.
const RIVAL_ILL_TAKE_THIS = ["RIVAL: I'll take this one, then!"];

// Cartridge Text_RivalReceivedMonFromOak. bufferspeciesname substitutes
// the rival's pick; we render per species so the message matches.
const RIVAL_RECEIVED_BY_SPECIES: Record<string, string[]> = {
  bulbasaur: ['BLUE received a BULBASAUR from PROF. OAK!'],
  charmander: ['BLUE received a CHARMANDER from PROF. OAK!'],
  squirtle: ['BLUE received a SQUIRTLE from PROF. OAK!'],
};

export const RIVAL_SPAWN = { x: 5, y: 4 } as const;

/** Outbound walk: rival spawn (5,4) → in front of the opposing ball at
 *  (pedestalX, 5). Routes SOUTH first, then east along open row 5 —
 *  row 4 x=8-10 are the collision-blocked starter-table tiles (the
 *  original row-4 X-align walked him over the table + remaining
 *  balls; first-friend playtest 2026-08-02). Exported so the
 *  waypoint-vs-collision regression test pins the shipped literals.
 *  npcWalkAlongPath is collision-blind by design (cartridge
 *  applymovement semantics) — path correctness lives HERE. */
export function rivalOutboundWaypoints(
  pedestalX: number,
): Array<{ x: number; y: number }> {
  return [
    { x: RIVAL_SPAWN.x, y: RIVAL_SPAWN.y + 1 }, // south 1 to open row 5
    { x: pedestalX, y: RIVAL_SPAWN.y + 1 }, // east along row 5 — front of ball
  ];
}

/** Return walk: (pedestalX, 5) → back to spawn (5,4). West along row 5,
 *  then north. Exported for the same regression test. */
export function rivalReturnWaypoints(): Array<{ x: number; y: number }> {
  return [
    { x: RIVAL_SPAWN.x, y: RIVAL_SPAWN.y + 1 }, // west to col 5
    { x: RIVAL_SPAWN.x, y: RIVAL_SPAWN.y }, // north to (5, 4)
  ];
}
const RIVAL_LOCAL_ID = 'LOCALID_OAKS_LAB_RIVAL';

const HIDE_FLAG_BY_SPECIES: Record<string, keyof ScriptCtx['FLAGS']> = {
  bulbasaur: 'HIDE_BULBASAUR_BALL',
  charmander: 'HIDE_CHARMANDER_BALL',
  squirtle: 'HIDE_SQUIRTLE_BALL',
};

/** In-flight re-entrancy guard. Replaces the old latch-at-ENTRY
 *  persisted flag: a WAKE mid-cinematic is denied HERE (session
 *  memory), while LAB_POST_PICK_DONE persists only when the cinematic
 *  actually completes — so a tab-close or stall mid-walk retries
 *  cleanly on the next load instead of stranding the save (the exact
 *  failure repairStrandedLabPostPick was built to undo). Third-
 *  playtest latch-ordering fix, applied to both lab cinematics. */
let postPickCinematicActive = false;

export const labPostPickHandler: Handler = async (ctx: ScriptCtx) => {
  if (postPickCinematicActive) return;
  const playerStarter = ctx.runState.getParty()[0]?.species;
  if (!playerStarter) return;
  const oakConfirm = OAK_CONFIRM_BY_SPECIES[playerStarter];
  const rivalPick = opposingStarterFor(playerStarter);
  if (!oakConfirm || !rivalPick) return;
  const rivalReceived = RIVAL_RECEIVED_BY_SPECIES[rivalPick.species];
  const opposingHideFlagKey = HIDE_FLAG_BY_SPECIES[rivalPick.species];
  if (!rivalReceived || !opposingHideFlagKey) return;

  postPickCinematicActive = true;
  try {
  await ctx.worldScene.runHandlerBracket(async () => {
    const walkSpeed = effectsReduced() ? 192 : 96;

    // Oak's confirmation per species (cartridge OakChoosingXxx).
    ctx.worldScene.showDialogue(oakConfirm, null);
    await ctx.worldScene.waitForDialogueClose();

    // Oak's energetic + travel advice (cartridge Text_OakThisMonIsEnergetic
    // + Text_OakCanReachNextTownWithMon).
    ctx.worldScene.showDialogue(OAK_ENERGETIC, null);
    await ctx.worldScene.waitForDialogueClose();
    ctx.worldScene.showDialogue(OAK_CAN_REACH_NEXT_TOWN, null);
    await ctx.worldScene.waitForDialogueClose();

    // Brief pre-walk pause matching cartridge delay between Oak's
    // advice and the rival walking to his pedestal.
    await ctx.worldScene.delay(500);

    // Hide the static rival NPC and spawn a handler-owned walker at
    // the same tile. refreshNpcVisibility removes the static sprite
    // synchronously BEFORE npcWalkAlongPath creates the walker so
    // there's no two-rivals overlap frame.
    ctx.runState.setFlag(ctx.FLAGS.HIDE_RIVAL_IN_LAB);
    ctx.worldScene.refreshNpcVisibility();

    const walker = await ctx.worldScene.npcWalkAlongPath({
      spriteKey: 'blue',
      startTile: RIVAL_SPAWN,
      waypoints: rivalOutboundWaypoints(rivalPick.pedestalX),
      pixelsPerSecond: walkSpeed,
    });

    // "I'll take this one, then!"
    ctx.worldScene.showDialogue(RIVAL_ILL_TAKE_THIS, null);
    await ctx.worldScene.waitForDialogueClose();

    // Opposing ball vanishes — rival "took" it. The other unpicked ball
    // (third one) stays visible forever per cartridge.
    ctx.runState.setFlag(ctx.FLAGS[opposingHideFlagKey]);
    ctx.worldScene.refreshNpcVisibility();

    // "BLUE received a XXX from PROF. OAK!"
    ctx.worldScene.showDialogue(rivalReceived, null);
    await ctx.worldScene.waitForDialogueClose();

    // Rival walks back to his spawn so future state-aware tap dialogue
    // resolves against the static NPC at (5, 4). Two waypoints: south-
    // align (5, 5) then north to (5, 4). expandWaypoints does X first
    // per waypoint, so going (pedestalX, 5) → (5, 5) → (5, 4) yields
    // west-then-north — clean retracement of the outbound path.
    await ctx.worldScene.npcWalkAlongPath({
      existingSprite: walker.sprite,
      spriteKey: 'blue', // ignored when existingSprite is set
      startTile: { x: rivalPick.pedestalX, y: RIVAL_SPAWN.y + 1 },
      waypoints: rivalReturnWaypoints(),
      pixelsPerSecond: walkSpeed,
    });

    // Sprite handoff (eng-review decision 6): clearFlag +
    // refreshNpcVisibility mounts the static rival NPC at (5, 4)
    // FIRST, THEN destroys the walker. Both sprites occupy (5, 4)
    // for the gap between these two ops — visually identical poses,
    // no invisible-rival frame.
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_RIVAL_IN_LAB);
    ctx.worldScene.refreshNpcVisibility();
    walker.sprite.destroy();

    // COMPLETION latch + CARTRIDGE STATE 3 — POST_PICK, written
    // together so a save can never carry the latch without the state
    // (the stranded-save window the old entry-latch created). Row-8
    // RivalBattleTrigger entries (varValue=3) become eligible.
    ctx.runState.setFlag(ctx.FLAGS.LAB_POST_PICK_DONE);
    setLabSceneState(LAB_SCENE_STATES.POST_PICK);

    ctx.worldScene.save();
  });
  } finally {
    postPickCinematicActive = false;
  }
};
